import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import { inputErrorState, inputFormValueState } from '../../stores/formState';
import { translateValue } from './TranslateRules';
import { completedComponentState } from '../../stores/completedComponentState';
import { getAddressQuery } from '../../stores/query';
import { calcCharWidth } from '../../utils/calcCharWidth';

export interface AddressInputProps {
  componentId: string;
  name: string;
  message?: string;
  optional: boolean;
  autoComplete?: string;
  postalCodeComponentId: string;
  optionalTextHidden?: boolean;
}

const AddressInput: React.FC<AddressInputProps> = props => {
  const [value, setValue] = useRecoilState(inputFormValueState(props.componentId));
  const [inputValue, setInputValue] = useState<string | null>(null);
  const postalCode = useRecoilValue(inputFormValueState(props.postalCodeComponentId));
  const { contents: address, state: addressState } = useRecoilValueLoadable(getAddressQuery(String(postalCode)))
  const [error, setError] = useRecoilState(inputErrorState(props.componentId));
  const inputRef = useRef<HTMLInputElement>(null);
  const [composingValue, setComposingValue] = useState<string | null>(null);

  useEffect(() => {
    if (addressState !== 'hasValue') return;
    if (address == null) return;
    const addressStr = `${address.prefecture ?? ''}${address.city ?? ''}${address.address ?? ''}`;
    setInputValue(prev => {
      if (prev != null && prev !== '') return prev;
      requestAnimationFrame(() => setValue(addressStr));
      return addressStr;
    });
  }, [addressState, address, address?.prefecture, address?.city, address?.address, setValue]);

  const setTranslatedValue = useCallback((inputValue: string) => {
    if (props.optional && inputValue === '') {
      setInputValue(null);
      setValue(null);
      setError(null);
      return;
    }

    const [value, error] = translateValue('text', inputValue);
    setInputValue(value != null ? value : inputValue);

    setValue(value);
    setError(error);
  }, [props.optional, setValue, setError, setInputValue])

  const onInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (composingValue != null) {
      setComposingValue(e.target.value);
    } else {
      setTranslatedValue(e.target.value);
    }
  }, [composingValue, setTranslatedValue]);

  const setCompleted = useSetRecoilState(completedComponentState(props.componentId));
  const onBlur = useCallback(() => {
    if (value == null || value === '') {
      setCompleted(false)
    } else {
      setCompleted(true)
    }
  }, [value, setCompleted]);

  const onCompositionStart = useCallback((e: React.CompositionEvent<HTMLInputElement>) => {
    if (!(e.target instanceof HTMLInputElement)) return;
    setComposingValue(e.target.value)
  }, []);
  const onCompositionEnd = useCallback(() => {
    if (composingValue == null) return;
    setTranslatedValue(composingValue)
    setComposingValue(null);
  }, [composingValue, setTranslatedValue]);


  const valueStr = composingValue != null ? composingValue : inputValue != null ? String(inputValue) : '';
  const size = Math.max(calcCharWidth(valueStr), 25);

  return (
    <label className={`text-input text ${error != null ? 'invalid' : ''}`}>
      <span>
      {props.message ? <span className="message">{props.message}</span> : ''}
      {!props.optional ? "(必須)" : props.optionalTextHidden ? "" : "(任意)"}
      </span>
      <span className="text-input-inner">
        <input
          type="text"
          name={props.name}
          size={size}
          value={valueStr}
          autoComplete={props.autoComplete}
          onChange={onInput}
          onCompositionStart={onCompositionStart}
          onCompositionEnd={onCompositionEnd}
          onBlur={onBlur}
          ref={inputRef}/>
      </span>
      <span className="error-message">{error}</span>
    </label>
  );
};

export default AddressInput;
